"use client";

import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { stegaClean } from "@sanity/client/stega";

/**
 * Renders a raw embed snippet pasted into Sanity (Vidalytics, Wistia, YouTube, …).
 *
 * Two things make this more than a `dangerouslySetInnerHTML`:
 *
 * 1. Markup assigned through `innerHTML` is parsed with its `<script>` elements
 *    flagged as "already started", so the browser never runs them. Each one has to
 *    be re-created for the embed's loader to execute at all.
 * 2. Snippets are usually a placeholder `<div id="…">` plus a script that targets
 *    that id, so the markup must be in the DOM before the script runs — which is
 *    why the scripts are swapped in only after the rest of the snippet is inserted.
 */
export default function EmbedCode({
  code,
  className,
}: {
  code?: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Draft mode stega-encodes strings with invisible characters; inside a <script>
  // those would corrupt the JS (and the element id it looks up), so strip them.
  const snippet = stegaClean(code ?? "")?.trim() ?? "";

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !snippet) return;

    container.innerHTML = snippet;

    container.querySelectorAll("script").forEach((original) => {
      const script = document.createElement("script");
      Array.from(original.attributes).forEach((attribute) =>
        script.setAttribute(attribute.name, attribute.value),
      );
      script.text = original.textContent ?? "";
      original.replaceWith(script);
    });

    return () => {
      container.innerHTML = "";
    };
  }, [snippet]);

  if (!snippet) return null;

  return <div ref={containerRef} className={cn("w-full", className)} />;
}
